import { useQuery } from '@redwoodjs/web'

import {
  PaymentFilterInputsQuery,
  PaymentFilterInputsQueryVariables,
  PaymentMethod,
  PaymentTransferType,
  RecordTaskFeesPageFilters,
  SearchPaymentTransfersFilters,
} from '../../../types/graphql'
import { CheckboxOption } from '../../components/form/Checkbox'
import { FilterInput } from '../../components/TableV2/types'
import { useMyOrg, useMyOrgActiveUsers } from '../../layouts/AppStateContextLayout/utils'
import { formatUserName } from '../../utils'
import { formatPaymentMethod } from '../../utils/fee'

// Payment methods that are always available for filtering, regardless of whether they've been used
const ALWAYS_AVAILABLE_PAYMENT_METHODS: PaymentMethod[] = ['Cash', 'Check']

const QueryPaymentFilterInputs = gql`
  query PaymentFilterInputsQuery($organizationId: Int!) {
    fees(organizationId: $organizationId) {
      id
      name
    }
    generalLedgerNumbers {
      ...GeneralLedgerNumberFragment
    }
    organization(id: $organizationId) {
      id
      finixMerchants {
        ...OrganizationFinixMerchantsFragment
      }
      paymentProvider
    }
    paymentMethodsUsedForOrg
  }
`

export interface Res {
  recordTaskFeesFilterInputs: FilterInput<RecordTaskFeesPageFilters>[]
  paymentTransfersFilterInputs: FilterInput<SearchPaymentTransfersFilters>[]
}

export const TransferTypeGovwellToDisplayMap: Record<PaymentTransferType, string> = {
  Debit: 'Customer Payment',
  Reversal: 'Refund',
  Credit: 'Credit',
  Adjustment: 'Adjustment',
  Dispute: 'Dispute',
  Fee: 'Fee',
  Settlement: 'Settlement',
  Reserve: 'Reserve',
  Unknown: 'Unknown',
}

const PaymentTransferTypeOptions: CheckboxOption<PaymentTransferType>[] = [
  {
    label: TransferTypeGovwellToDisplayMap['Debit'],
    value: 'Debit',
  },
  {
    label: TransferTypeGovwellToDisplayMap['Reversal'],
    value: 'Reversal',
  },
  {
    label: TransferTypeGovwellToDisplayMap['Credit'],
    value: 'Credit',
  },
  {
    label: TransferTypeGovwellToDisplayMap['Adjustment'],
    value: 'Adjustment',
  },
  {
    label: TransferTypeGovwellToDisplayMap['Dispute'],
    value: 'Dispute',
  },
]

const basePaymentTransferInputs: FilterInput<SearchPaymentTransfersFilters>[] = [
  {
    fieldName: 'transactionDate',
    type: 'Date',
    label: 'Transaction Date',
  },
  {
    fieldName: 'type',
    type: 'Arr',
    label: 'Type',
    checkboxOptions: PaymentTransferTypeOptions,
  },
]

const BaseRecordTaskFeeInputs: FilterInput<RecordTaskFeesPageFilters>[] = [
  {
    fieldName: 'requestedDateRange',
    label: 'Requested Date',
    type: 'Date',
  },
  {
    fieldName: 'paidDateRange',
    label: 'Payment Date',
    type: 'Date',
  },
]

export const usePaymentFilterInputs = () => {
  const organizationId = useMyOrg()?.id ?? -1
  const res = useQuery<PaymentFilterInputsQuery, PaymentFilterInputsQueryVariables>(
    QueryPaymentFilterInputs,
    {
      variables: {
        organizationId,
      },
    }
  )

  const users = useMyOrgActiveUsers()

  if (!res.data?.fees?.length && !res?.data?.generalLedgerNumbers?.length) {
    return {
      recordTaskFeesFilterInputs: BaseRecordTaskFeeInputs,
      paymentTransfersFilterInputs: basePaymentTransferInputs,
    }
  }

  const {
    data: { fees, generalLedgerNumbers, paymentMethodsUsedForOrg },
  } = res
  const finixMerchants = res.data?.organization?.finixMerchants || []
  const paymentProvider = res.data?.organization?.paymentProvider ?? ''

  const finixMerchantIdOptions = finixMerchants.map((fm) => ({
    label: fm.description,
    value: fm.id,
  }))

  const paymentTransferFilterFinixMerchant: FilterInput<SearchPaymentTransfersFilters> = {
    fieldName: 'oranizationFinixMerchantIds',
    label: 'Merchant',
    type: 'Arr',
    checkboxOptions: finixMerchantIdOptions,
  }

  const glFilterForPaymentTransfers: FilterInput<SearchPaymentTransfersFilters> = {
    fieldName: 'generalLedgerNumberIds',
    label: 'GL Number',
    type: 'Arr',
    checkboxOptions: generalLedgerNumbers.map((gl) => ({
      label: gl.description ? `${gl.glNumber} (${gl.description})` : gl.glNumber,
      value: gl.id,
    })),
  }

  const paymentTransferFilterProcessedBy: FilterInput<SearchPaymentTransfersFilters> = {
    fieldName: 'processedByUserIds',
    label: 'Processed By',
    type: 'Arr',
    checkboxOptions: users.map((u) => ({
      label: formatUserName(u),
      value: u.id,
    })),
  }

  // Combine payment methods from backend with always-available ones, ensuring uniqueness
  const allPaymentMethods = [
    ...new Set([...(paymentMethodsUsedForOrg || []), ...ALWAYS_AVAILABLE_PAYMENT_METHODS]),
  ].sort((a, b) =>
    formatPaymentMethod(a, paymentProvider).localeCompare(formatPaymentMethod(b, paymentProvider))
  )

  const paymentMethodFilters: FilterInput<SearchPaymentTransfersFilters> = {
    fieldName: 'paymentMethod',
    type: 'Arr',
    label: 'Payment Method',
    checkboxOptions: allPaymentMethods.map((paymentMethod) => ({
      label: formatPaymentMethod(paymentMethod, paymentProvider),
      value: paymentMethod,
    })),
  }

  const paymentTransfersFilters: FilterInput<SearchPaymentTransfersFilters>[] = [
    ...basePaymentTransferInputs,
    ...(allPaymentMethods.length ? [paymentMethodFilters] : []),
    ...(generalLedgerNumbers?.length ? [glFilterForPaymentTransfers] : []),
    paymentTransferFilterProcessedBy,
    ...(finixMerchantIdOptions?.length > 1 ? [paymentTransferFilterFinixMerchant] : []),
  ]

  const glFilterForRecordTaskFees: FilterInput<RecordTaskFeesPageFilters> = {
    fieldName: 'glNumber',
    label: 'GL Number',
    type: 'Arr',
    checkboxOptions: generalLedgerNumbers.map((gl) => ({
      label: gl.description ? `${gl.glNumber} (${gl.description})` : gl.glNumber,
      value: gl.id,
    })),
  }

  const recordTaskFeesFilterFinixMerchant: FilterInput<RecordTaskFeesPageFilters> = {
    fieldName: 'oranizationFinixMerchantIds',
    label: 'Merchant',
    type: 'Arr',
    checkboxOptions: finixMerchantIdOptions,
  }

  const recordTaskFeeFilterInputs: FilterInput<RecordTaskFeesPageFilters>[] = [
    {
      fieldName: 'feeId',
      label: 'Fee',
      type: 'Arr',
      checkboxOptions: fees.map((f) => ({
        label: f.name,
        value: f.id,
      })),
    },
    ...BaseRecordTaskFeeInputs,
    ...(generalLedgerNumbers?.length ? [glFilterForRecordTaskFees] : []),
    ...(finixMerchantIdOptions?.length > 1 ? [recordTaskFeesFilterFinixMerchant] : []),
  ]

  return {
    recordTaskFeesFilterInputs: recordTaskFeeFilterInputs,
    paymentTransfersFilterInputs: paymentTransfersFilters,
  }
}
